import React, { memo, useMemo } from 'react'
import CarrierStatus from 'components/marketplace/CarrierStatus'
import SafetyStatus from 'components/marketplace/SafetyStatus'
import moment from 'moment'
import { datetimeFormatter } from 'transfix-ui/components/FormattedDateTime'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Typography } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import { TCarrierStatus, SAFETY_RATING } from 'types/carrier'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { DiverseCarrierIcon, SmartwayIcon, UserIcon, PreferredIcon } from 'transfix-icons'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { FMCSA_STATUS_ICON_MAP } from 'constants/carriers'
import { HeaderActions } from '../HeaderActions'
import HighwaySafetyStatus from './HighwaySafetyStatus'
import { ICarrierPageFragment } from './graphql/CarrierPageFragment'

interface ICarrierProfileHeaderProps {
  carrierProfile: ICarrierPageFragment
}

const CarrierProfileHeader = memo<ICarrierProfileHeaderProps>(({ carrierProfile }) => {
  const {
    carrierProfile: profileInfo,
    deletedAt,
    fmcsaRating,
    isTrusted,
    name,
    primaryCarrierManager,
    state,
  } = carrierProfile

  const { certifications = [], smartWayRegistration } = profileInfo || {}
  const isDiverseCarrier = certifications.length > 0
  const isVerifiedDiverseCarrier = isDiverseCarrier && certifications.some(cert => cert.isVerified)
  const isSmartWayCarrier = smartWayRegistration && smartWayRegistration.isRegistered
  const formattedDeletedAtDate = useMemo(
    () =>
      deletedAt
        ? datetimeFormatter({
            datetime: deletedAt,
            displayTimezone: moment.tz.guess() as AllowedTimezones,
            format: 'dateAndYearFullDateAlways',
            showTimezone: false,
            abbreviated: true,
          })
        : null,
    []
  )

  const validRating = fmcsaRating && FMCSA_STATUS_ICON_MAP[fmcsaRating as SAFETY_RATING]

  const isHighwayMilestoneFourComplianceTabEnabled = useFeatureFlag(
    'highwayMilestoneFourComplianceTab'
  )

  return (
    <Box display='grid'>
      <Box display='flex' justifyContent='space-between' alignItems='center' pb='8px'>
        <Typography variant='h2' data-test='carrier-name'>
          {name}
          {isTrusted && (
            <Tooltip
              position='right'
              trigger={
                <span>
                  <PreferredIcon size='large' className='ml4 mb0' />
                </span>
              }
              type='dark'>
              Preferred Carrier
            </Tooltip>
          )}
        </Typography>
        <HeaderActions />
      </Box>
      <Box display='flex' minHeight='48px' gap='8px 24px' flexWrap='wrap' alignItems='center'>
        {state && <CarrierStatus status={state as TCarrierStatus} displayStatus />}

        {!isHighwayMilestoneFourComplianceTabEnabled && fmcsaRating && validRating && (
          <SafetyStatus status={fmcsaRating as SAFETY_RATING} />
        )}
        {isHighwayMilestoneFourComplianceTabEnabled && <HighwaySafetyStatus />}

        <Box display='flex' alignItems='center' gap='4px' width='max-content'>
          <UserIcon color='info' size='large' />
          {primaryCarrierManager && <strong>{primaryCarrierManager.fullName}</strong>}
          <Typography component='span' color='textSecondary' variant='body2'>
            {primaryCarrierManager ? 'Primary Carrier Manager' : 'No Primary Manager'}
          </Typography>
        </Box>

        {isDiverseCarrier && (
          <span>
            <DiverseCarrierIcon color='info' size='large' verticalAlign='text-top' />
            &nbsp;
            <Typography component='span' color='textPrimary' variant='subtitle1'>
              Diverse Carrier
            </Typography>
            &nbsp;
            <Typography component='span' color='textSecondary' variant='body2'>
              {isVerifiedDiverseCarrier ? '(verified)' : '(not verified)'}
            </Typography>
          </span>
        )}

        {isSmartWayCarrier && (
          <span>
            <SmartwayIcon color='info' size='large' verticalAlign='text-top' />
            &nbsp;
            <Typography component='span' color='textPrimary' variant='subtitle1'>
              SmartWay
            </Typography>
          </span>
        )}
      </Box>
      {formattedDeletedAtDate && (
        <InlineMessage
          message={`This account was deleted on ${formattedDeletedAtDate}`}
          type={INLINE_MESSAGE_VARIANTS.WARNING}
        />
      )}
    </Box>
  )
})

export default CarrierProfileHeader
